import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.beni-gipserei.ch';
const SITE_NAME = 'BENI Gipsergeschäft GmbH';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_a13cc131-3351-41ce-87d0-56be3a0fcef6/artifacts/sac0lcc8_Logo.png';
const PHONE = '+41 79 359 18 62';

export const SEO = ({ title, description, path = '', keywords = '', schema, breadcrumbs }) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Gipser, Maler & Trockenbau in Aesch, Basel`;
  const fullUrl = `${SITE_URL}${path}`;

  const defaultDescription = 'BENI Gipsergeschäft GmbH – Professionelle Gipserarbeiten, Malerarbeiten, Trockenbau, Aussenfassaden und Innenausbau in Aesch und der Region Basel. Umbau, Neubau, Sanierungen. Kostenlose Beratung!';
  const desc = description || defaultDescription;

  const defaultKeywords = 'Gipser Aesch, Maler Aesch, Trockenbau Aesch, Gipser Basel, Maler Basel, Gipserarbeiten, Malerarbeiten, Trockenbau, Aussenfassaden, Innenausbau, Fassadenarbeiten Basel-Landschaft, Sanierung Aesch, Umbau Basel, Neubau Gipser, BENI Gipsergeschäft, Gipser Reinach, Gipser Münchenstein, Gipser Muttenz, Gipser Binningen, Gipser Oberwil, Gipser Therwil, Gipser Allschwil, Gipser Laufen, Gipser Büsserach, Verputzarbeiten Basel, Stuckarbeiten Aesch, Fassadensanierung Basel, Wärmedämmung Aesch, Innenverputz Basel-Landschaft, Aussenverputz Aesch';
  const kw = keywords || defaultKeywords;

  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`
    }))
  } : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <link rel="canonical" href={fullUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="de_CH" />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:image:alt" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />

      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
};

// FAQ Schema für Google Rich Results
export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Welche Dienstleistungen bietet BENI Gipsergeschäft an?",
      "acceptedAnswer": { "@type": "Answer", "text": "Wir bieten professionelle Gipserarbeiten, Malerarbeiten, Trockenbau, Aussenfassaden und Innenausbau an. Für Umbau, Neubau und Sanierungen von Ein- und Mehrfamilienhäusern, Büro- und Verwaltungsgebäuden sowie Gewerbe- und Industriebauten." }
    },
    {
      "@type": "Question",
      "name": "In welchen Regionen ist BENI Gipsergeschäft tätig?",
      "acceptedAnswer": { "@type": "Answer", "text": "Unser Einzugsgebiet umfasst Aesch, Basel, Reinach, Münchenstein, Muttenz, Laufen, Büsserach, Binningen, Oberwil, Therwil und Allschwil im Kanton Basel-Landschaft und Basel-Stadt." }
    },
    {
      "@type": "Question",
      "name": "Wie kann ich eine Offerte anfordern?",
      "acceptedAnswer": { "@type": "Answer", "text": "Sie können uns telefonisch unter +41 79 359 18 62 erreichen, per E-Mail an beni-gipser@bluewin.ch kontaktieren oder das Kontaktformular auf unserer Website nutzen. Die Beratung und Offerte sind kostenlos." }
    },
    {
      "@type": "Question",
      "name": "Wo befindet sich BENI Gipsergeschäft?",
      "acceptedAnswer": { "@type": "Answer", "text": "Unser Firmensitz befindet sich an der Hauptstrasse 57, 4147 Aesch BL, Schweiz." }
    },
    {
      "@type": "Question",
      "name": "Macht BENI Gipsergeschäft auch Fassadenarbeiten?",
      "acceptedAnswer": { "@type": "Answer", "text": "Ja, wir bieten professionelle Fassadenarbeiten an, einschliesslich Fassadensanierung, Wärmedämmung (VAWD/WDVS), Aussenverputz und kreative Fassadengestaltung." }
    },
    {
      "@type": "Question",
      "name": "Bietet BENI Gipsergeschäft kostenlose Beratung an?",
      "acceptedAnswer": { "@type": "Answer", "text": "Ja, wir bieten eine kostenlose Besichtigung vor Ort und unverbindliche Beratung für alle unsere Dienstleistungen an." }
    }
  ]
};

// Service Schema
export const getServiceSchema = (serviceName, serviceDescription) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": serviceName,
  "provider": {
    "@type": "LocalBusiness",
    "name": SITE_NAME,
    "telephone": PHONE,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Hauptstrasse 57",
      "addressLocality": "Aesch",
      "postalCode": "4147",
      "addressRegion": "BL",
      "addressCountry": "CH"
    }
  },
  "areaServed": {
    "@type": "GeoCircle",
    "geoMidpoint": { "@type": "GeoCoordinates", "latitude": "47.4713", "longitude": "7.5914" },
    "geoRadius": "30000"
  },
  "description": serviceDescription
});
